import {Injectable} from '@angular/core';
import {ConnectorService} from './connector.service';
import {Observable} from 'rxjs';
import {MemberModel, UserRightsModel} from '../../models/user.model';
import {PaginationModel} from '../../models/file-table.model';
import {MenuModel, SubMenuModel} from '../../models/menu.model';

// export interface VerifyTokenResponse {
//     member: MemberModel;
//     project: any;
// }

export interface AccessTokenResponse {
    access_token: string;
    expires_in: string;
    message: string;
    valid_for_seconds: number;
    member: MemberModel;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService extends ConnectorService {
    /**
     * Логин
     * @param data
     */
    login(data: Partial<{ email: string; passwd: string; }>): Observable<AccessTokenResponse> {
        return this.post('instance/dag/auth/login', data);
    }

    register(data: {
        email: string,
        passwd: string,
        invite_code: string,
        firstname: string,
        lastname: string,
        middlename: string,
    }): Observable<AccessTokenResponse> {
        return this.post('instance/dag/auth/invite', data);
    }

    /**
     * Обновление токена
     */
    updateAccessToken(): Observable<AccessTokenResponse> {
        return this.put('instance/dag/auth/access');
    }

    /**
     * Валидация токена
     */
    // verifyToken(): Observable<VerifyTokenResponse> {
    //     return this.get('instance/dag/auth/info');
    // }

    /**
     * Профиль пользователя по GUID
     * @param guid
     */
    getUserByGUID(guid = null): Observable<MemberModel> {
        return this.get('instance/dag/users/profile' + (guid ? '?guid=' + guid : ''));
    }

    /**
     * Все пользователи проекта
     */
    getAllUsers(guids?: string): Observable<{ users: MemberModel[] }> {
        return this.get('instance/dag/users/all', {guids});
    }

    /**
     * Все пользователи проекта с пагинацией
     * @param pagination
     */
    getAllUsersWithPagination(pagination: PaginationModel): Observable<{
        data: MemberModel[],
        current_page: number,
        page_count: number
    }> {
        return this.get('instance/dag/users/' +
            '?limit=' + pagination.limit +
            '&current_page=' + pagination.current_page);
    }

    /**
     * Выслать инвайт новому пользователю в проект
     * @param data
     */
    inviteUserInProject(data: any): Observable<any> {
        return this.post('instance/dag/users', data);
    }

    /**
     * Права пользователя, если guid не передан, то права текущего пользователя
     * @param userGuid
     */
    getUserRights(userGuid = null): Observable<{
        message: string,
        rights: UserRightsModel[]
    }> {
        const data = userGuid ? {guid: userGuid} : {};
        return this.get('instance/dag/rights/departments/user', data);
    }

    /**
     * Установка прав пользователя
     * @param data
     * TODO расписать
     */
    setUserRights(data: { acc_type: any; instance_type: any; instance_id: any; guid: string; }): Observable<{
        message: string
    }> {
        return this.put('instance/dag/rights/departments/user', data);
    }

    /**
     * Удаление пользовательских прав
     * @param data
     * TODO расписать
     */
    deleteUserRights(data: { instance_id: number; instance_type: string; guid: string; }): Observable<{
        message: string
    }> {
        return this.delete('instance/dag/rights/departments/user/', data);
    }

    /**
     * Изменение типа пользователя (внешний/внутренний) только если пользователь не админ (любой)
     * @param userGuid
     * @param observe
     */
    changeUserObservation(userGuid: string, observe: string): Observable<{ message: string }> {
        return this.put('instance/dag/users/observation', {guid: userGuid, observ: observe});
    }

    /**
     * Выдача пользователю крипто-ключа
     * @param data
     */
    giveToUserProjectCrypto(data: { guid: string; provider: string; }): Observable<{ message: string }> {
        return this.post('global/user/crypto', data);
    }

    /**
     * Удаление пользователя из проекта
     *
     * @param userGuid
     */
    deleteUserFromProject(userGuid: string): Observable<{ message: string }> {
        return this.delete('instance/dag/users', {user_guid: userGuid});
    }

    /**
     * Изменение ФИО пользователя
     * @param data
     * @returns
     */
    editUserProfile(data: {
        firstname: string;
        middlename: string;
        lastname: string;
        phone: string,
        lang: string
    }): Observable<any> {
        return this.put('instance/dag/users/profile', data);
    }

    /**
     * Обновление аватарки
     * @param photo
     */
    updateAvatar(photo: { avatar: Blob | MediaSource }): Observable<any> {
        const form = new FormData();
        for (const dataKey of Object.keys(photo)) {
            form.append(dataKey, photo[dataKey]);
        }
        return this.put('instance/dag/users/profile/avatar', form, false);
    }

    /**
     * Получение аватара
     * @param min - размер
     * @param guid - гуид пользователя
     */
    getAvatar(min: boolean, guid: string): Observable<any> {
        return this.getBodyFile('instance/dag/users/profile/avatar', {min, user_guid: guid});
    }

    /**
     * Удаление аватара пользователя
     */
    deleteAvatar(): Observable<any> {
        return this.delete('instance/dag/users/profile/avatar');
    }

    /**
     * Изменение пароля пользователя
     * @param data
     * @returns
     */
    updatePassword(data: { passwd_old: string; passwd_new: string; }): Observable<AccessTokenResponse> {
        return this.put('instance/dag/users/password', data);
    }

    /**
     * Изменение PIN-кода
     * @param data
     */
    updatePin(data: { old_pin: string, new_pin: string }): Observable<any> {
        return this.put('instance/dag/users/pin_valid', data);
    }

    /**
     * Изменение экстренного PIN-кода
     * @param data
     */
    updateExtraPin(data: { old_pin: string, new_pin: string }): Observable<any> {
        return this.put('instance/dag/users/pin_extra', data);
    }

    /**
     * получение левого столбца меню проекта
     */
    getMenu(isAll = false): Observable<{ project_settings: MenuModel[], submenu_items: SubMenuModel[] }> {
        return this.get('instance/settings/menu_settings/get_settings', isAll ? {'all_structure': isAll} : {});
    }

    saveMenu(data: string): Observable<{ project_settings: MenuModel[], submenu_items: SubMenuModel[] }> {
        return this.put('instance/settings/menu_settings', data, false);
    }

    editMemberPosition(data: { member_guid: string, position: string }): Observable<any> {
        return this.put('instance/dag/users/position', data);
    }

    /**
     * Обновление названия, типа проекта и типа организации в проекте
     * @param data
     * {
     *      title: string,  - название проекта
     *      project_type: 'organization' | 'person',  - тип проекта
     *      project_subtype: string - тип организации (ООО, ЗАО, и тд)
     * }
     */
    updateProject(
        data: {
            title: string,
            project_type: string,
            project_subtype: string
        }
    ): Observable<any> {
        return this.put('instance/settings/project_settings/update', data);
    }
}
